import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import analyticsService from '../services/analyticsService.js';
import D3LineChart from '../../project750k/components/charts/D3LineChart.jsx';
import D3BarChart from '../../project750k/components/charts/D3BarChart.jsx';
// Using inline components for now - can be replaced with shared components later
import './Dashboard.css';

// ApplyPresetModal component for the apply preset flow
const ApplyPresetModal = ({ preset, campaign, senders, onClose, onApply, applying, getRiskStars, getRiskLabel }) => {
  const [startDate, setStartDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [maxVolume, setMaxVolume] = useState(preset.maxVolumePerMailbox || 75);
  const [selectedSenders, setSelectedSenders] = useState(
    senders.reduce((acc, sender) => ({ ...acc, [sender]: true }), {})
  );

  const activeSenders = Object.entries(selectedSenders)
    .filter(([_, selected]) => selected)
    .map(([sender]) => sender);

  const generatePreview = () => {
    if (!preset.schedule) return [];
    const rows = [];
    const maxDay = Math.max(...preset.schedule.map(s => s.day));

    for (let day = 1; day <= Math.min(7, maxDay); day++) {
      const scheduleItem = preset.schedule.find(s => s.day === day) ||
        preset.schedule.filter(s => s.day <= day).pop();

      if (!scheduleItem) continue;

      const date = new Date(startDate);
      date.setDate(date.getDate() + day - 1);
      const volume = Math.round(maxVolume * scheduleItem.pct / 100);

      rows.push({
        date: date.toISOString().split('T')[0],
        volume,
        pct: scheduleItem.pct
      });
    }
    return rows;
  };

  const preview = generatePreview();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content apply-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Apply "{preset.name}" to Campaign</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="apply-form">
            <div className="form-group">
              <label>Campaign:</label>
              <input type="text" value={campaign.name} disabled className="form-input" />
            </div>

            <div className="form-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="form-input"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label>Max Volume/Mailbox:</label>
              <input
                type="number"
                value={maxVolume}
                onChange={(e) => setMaxVolume(parseInt(e.target.value) || 0)}
                className="form-input"
                min={1}
                max={500}
              />
              <span className="form-hint">Preset default: {preset.maxVolumePerMailbox}</span>
            </div>

            <div className="form-group">
              <label>Mailboxes to include:</label>
              <div className="sender-checkboxes">
                {senders.map((sender) => (
                  <label key={sender} className="sender-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedSenders[sender] || false}
                      onChange={(e) => setSelectedSenders(prev => ({
                        ...prev,
                        [sender]: e.target.checked
                      }))}
                    />
                    <span>{sender}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <h4>Generated Schedule Preview (first 7 days):</h4>
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {activeSenders.map((sender, idx) => (
                    <th key={idx} title={sender}>{sender.split('@')[0]}@</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.date}</td>
                    {activeSenders.map((_, sidx) => (
                      <td key={sidx}>{row.volume}</td>
                    ))}
                    <td><strong>{row.volume * activeSenders.length}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={applying}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => onApply({
              startDate,
              maxVolumePerMailbox: maxVolume,
              mailboxes: activeSenders
            })}
            disabled={applying || activeSenders.length === 0}
          >
            {applying ? 'Applying...' : 'Apply & Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [vpsStats, setVpsStats] = useState({
    overview: { totalSent: 0, totalBounced: 0, totalReplied: 0, totalFailed: 0, totalCampaigns: 0 },
    campaigns: []
  });
  const [sendingPlan, setSendingPlan] = useState(null);
  const [sendingPlanParsed, setSendingPlanParsed] = useState({ headers: [], rows: [], senders: [] });
  const [loading, setLoading] = useState(true);
  const [loadingCampaignData, setLoadingCampaignData] = useState(false);
  const [error, setError] = useState(null);

  // Preset state
  const [presets, setPresets] = useState([]);
  const [presetsExpanded, setPresetsExpanded] = useState(false);
  const [presetFilter, setPresetFilter] = useState('all');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [showApplyFlow, setShowApplyFlow] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(false);
  const [campaignTriggers, setCampaignTriggers] = useState(null);

  // Template state
  const [campaignTemplate, setCampaignTemplate] = useState(null);
  const [templateExpanded, setTemplateExpanded] = useState(false);

  // Historical sending stats
  const [historicalStats, setHistoricalStats] = useState(null);

  // Campaign selector state
  const [selectedCampaignIndex, setSelectedCampaignIndex] = useState(0);

  // Date range selector state
  const [dateRange, setDateRange] = useState('all'); // 'today', 'yesterday', '7days', '30days', 'all'

  // Campaign events and analytics data
  const [campaignEvents, setCampaignEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Provider breakdown data
  const [providerBreakdown, setProviderBreakdown] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);

  const navigate = useNavigate();

  // Get selected campaign
  const selectedCampaign = vpsStats?.campaigns?.[selectedCampaignIndex] || null;

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get date range parameters for filtering VPS stats
      const { startDate, endDate } = getDateRangeParams();

      const [vpsData, presetsData] = await Promise.all([
        analyticsService.getVpsStats({ startDate, endDate }).catch(err => {
          console.log('VPS stats not available:', err.message);
          return { overview: { totalSent: 0, totalBounced: 0, totalReplied: 0, totalFailed: 0, totalDelivered: 0, totalCampaigns: 0 }, campaigns: [] };
        }),
        analyticsService.getPresets().catch(err => {
          console.log('Presets not available:', err.message);
          return [];
        })
      ]);

      setVpsStats(vpsData);
      setPresets(presetsData);

      // Campaign-specific data (sending plan, triggers, template, historical stats)
      // is now loaded by the separate useEffect that watches selectedCampaignIndex

      setError(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Load campaign-specific data when selected campaign changes
  useEffect(() => {
    const loadCampaignSpecificData = async () => {
      if (!vpsStats.campaigns || vpsStats.campaigns.length === 0) return;

      const campaign = vpsStats.campaigns[selectedCampaignIndex];
      if (!campaign) return;

      const campaignId = campaign.campaignId;

      setLoadingCampaignData(true);
      try {
        const [planData, triggersData, templateData, historyData] = await Promise.all([
          analyticsService.getSendingPlan(campaignId).catch(() => null),
          analyticsService.getCampaignTriggers(campaignId).catch(() => null),
          analyticsService.getCampaignTemplate(campaignId).catch(() => null),
          analyticsService.getHistoricalSendingStats(campaignId).catch(() => null)
        ]);

        setSendingPlan(planData);
        if (planData?.csvData) {
          setSendingPlanParsed(analyticsService.parseSendingPlanCsv(planData.csvData));
        } else {
          setSendingPlanParsed({ headers: [], rows: [], senders: [] });
        }
        setCampaignTriggers(triggersData);
        setCampaignTemplate(templateData);
        setHistoricalStats(historyData);
      } catch (err) {
        console.log('Error loading campaign-specific data:', err.message);
      } finally {
        setLoadingCampaignData(false);
      }
    };

    loadCampaignSpecificData();
  }, [selectedCampaignIndex, vpsStats.campaigns]);

  const handleCreateCampaign = () => {
    navigate('/marketing/create');
  };

  const handleDownloadCsv = () => {
    if (!sendingPlan?.csvData) return;

    const blob = new Blob([sendingPlan.csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sending_plan.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getTodayRow = () => {
    const today = new Date().toISOString().split('T')[0];
    return sendingPlanParsed.rows.find(row => row.date === today);
  };

  // Merge historical data with planned schedule
  const getMergedSchedule = () => {
    const today = new Date().toISOString().split('T')[0];
    const merged = [];
    const historicalDates = new Set();

    // Get all unique senders from planned schedule
    const allSenders = sendingPlanParsed.senders || [];

    // First, collect all historical dates (past dates with actual send data)
    if (historicalStats?.history) {
      historicalStats.history.forEach(h => {
        if (h.date < today) {
          // Convert volumes object to array matching sender order
          const volumes = allSenders.map(sender => h.volumes[sender] || 0);
          merged.push({
            date: h.date,
            volumes,
            total: h.total,
            notes: '',
            isHistorical: true
          });
          historicalDates.add(h.date);
        }
      });
    }

    // Add planned rows - skip past dates that have historical data, show today and future
    sendingPlanParsed.rows.forEach(row => {
      // For past dates: prefer historical data (skip if we have actual data)
      // For today and future: always show planned data
      if (row.date >= today || !historicalDates.has(row.date)) {
        merged.push({
          ...row,
          isHistorical: false
        });
      }
    });

    // Sort by date
    return merged.sort((a, b) => a.date.localeCompare(b.date));
  };

  // Preset helpers
  const getRiskStars = (riskTrack) => {
    const starMap = { safe: 5, medium: 3, aggressive: 1 };
    const count = starMap[riskTrack] || 3;
    return '★'.repeat(count) + '☆'.repeat(5 - count);
  };

  const getRiskLabel = (riskTrack) => {
    const labels = { safe: 'Safe', medium: 'Medium', aggressive: 'Aggressive' };
    return labels[riskTrack] || riskTrack;
  };

  const filteredPresets = presets.filter(preset => {
    if (presetFilter === 'all') return true;
    return preset.riskTrack === presetFilter;
  });

  const handleViewPreset = async (preset) => {
    try {
      const fullPreset = await analyticsService.getPreset(preset.presetId);
      setSelectedPreset(fullPreset);
      setShowPresetModal(true);
    } catch (err) {
      console.error('Error loading preset details:', err);
    }
  };

  const handleApplyPreset = async (preset) => {
    try {
      const fullPreset = await analyticsService.getPreset(preset.presetId);
      setSelectedPreset(fullPreset);
      setShowApplyFlow(true);
    } catch (err) {
      console.error('Error loading preset details:', err);
    }
  };

  const handleConfirmApply = async (config) => {
    if (!selectedPreset || !selectedCampaign) return;

    setApplyingPreset(true);
    try {
      const campaignId = selectedCampaign.campaignId;
      await analyticsService.applyPreset(campaignId, selectedPreset.presetId, config);
      setShowApplyFlow(false);
      setSelectedPreset(null);
      // Reload data to show updated schedule
      await loadDashboardData();
    } catch (err) {
      console.error('Error applying preset:', err);
      alert('Failed to apply preset: ' + (err.response?.data?.error || err.message));
    } finally {
      setApplyingPreset(false);
    }
  };

  const getTriggerStatus = (trigger, campaign) => {
    if (!campaign?.stats) return { status: 'ok', value: 0 };

    const stats = campaign.stats;
    let currentValue = 0;

    switch (trigger.metric) {
      case 'hard_bounce_rate':
        currentValue = stats.sent > 0 ? (stats.bounced || 0) / stats.sent : 0;
        break;
      case 'soft_bounce_rate':
        currentValue = stats.sent > 0 ? ((stats.softBounced || 0) / stats.sent) : 0;
        break;
      case 'spam_complaint_rate':
        currentValue = stats.sent > 0 ? ((stats.spamComplaints || 0) / stats.sent) : 0;
        break;
      default:
        currentValue = 0;
    }

    const isTriggered = trigger.operator === '>=' ? currentValue >= trigger.threshold : currentValue > trigger.threshold;
    const isWarning = trigger.operator === '>='
      ? currentValue >= trigger.threshold * 0.8
      : currentValue > trigger.threshold * 0.8;

    return {
      status: isTriggered ? 'triggered' : isWarning ? 'warning' : 'ok',
      value: currentValue
    };
  };

  // Date range helper functions
  const getDateRangeParams = useCallback(() => {
    const now = new Date();

    // Helper to format date as YYYY-MM-DD using local timezone
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Create today at midnight local time
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    switch (dateRange) {
      case 'today':
        return { startDate: formatDateLocal(today), endDate: formatDateLocal(today) };
      case 'yesterday':
        return { startDate: formatDateLocal(yesterday), endDate: formatDateLocal(yesterday) };
      case '7days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
        return { startDate: formatDateLocal(sevenDaysAgo), endDate: formatDateLocal(today) };
      case '30days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
        return { startDate: formatDateLocal(thirtyDaysAgo), endDate: formatDateLocal(today) };
      case 'all':
      default:
        return { startDate: null, endDate: null };
    }
  }, [dateRange]);

  const getDateRangeLabel = useCallback(() => {
    switch (dateRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case '7days': return 'Last 7 Days';
      case '30days': return 'Last 30 Days';
      case 'all': return 'All Time';
      default: return 'All Time';
    }
  }, [dateRange]);

  // Load campaign events for analytics
  const loadCampaignEvents = useCallback(async (campaignId) => {
    if (!campaignId) return;

    setLoadingEvents(true);
    try {
      const { startDate, endDate } = getDateRangeParams();
      console.log('[loadCampaignEvents] Date params:', { startDate, endDate, dateRange });

      // Check if this is a VPS campaign - VPS campaigns have source: 'vps'
      const isVpsCampaign = selectedCampaign?.source === 'vps';
      console.log('[loadCampaignEvents] Is VPS campaign:', isVpsCampaign, 'source:', selectedCampaign?.source);

      // Use the appropriate endpoint based on campaign type
      const endpoint = isVpsCampaign ? `/marketing/vps/${campaignId}/events` : `/marketing/${campaignId}/events`;
      console.log('[loadCampaignEvents] Calling endpoint:', endpoint);

      const events = isVpsCampaign
        ? await analyticsService.getVpsCampaignEvents(campaignId, {
            startDate,
            endDate,
            limit: 10000
          })
        : await analyticsService.getCampaignEvents(campaignId, {
            type: undefined,
            limit: 10000
          });

      console.log('[loadCampaignEvents] Received events:', events?.length || 0);

      // For non-VPS campaigns, filter events by date range if specified
      // (VPS endpoint already handles date filtering)
      let filteredEvents = events || [];
      if (!isVpsCampaign && startDate && endDate) {
        filteredEvents = filteredEvents.filter(event => {
          const eventDate = event.timestamp ? new Date(event.timestamp).toISOString().split('T')[0] : null;
          return eventDate >= startDate && eventDate <= endDate;
        });
      }

      console.log('[loadCampaignEvents] Filtered events:', filteredEvents.length);
      setCampaignEvents(filteredEvents);

      // Compute provider breakdown from events
      computeProviderBreakdown(filteredEvents);
    } catch (err) {
      console.log('Error loading campaign events:', err.message);
      setCampaignEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [selectedCampaign, dateRange]);

  // Extract provider from email address
  const extractProvider = (email) => {
    if (!email || !email.includes('@')) return 'other';
    const domain = email.split('@')[1]?.toLowerCase() || '';

    if (domain.includes('gmail.')) return 'gmail';
    if (domain.includes('googlemail.')) return 'gmail';
    if (domain.includes('outlook.') || domain.includes('hotmail.') || domain.includes('live.')) return 'outlook';
    if (domain.includes('yahoo.')) return 'yahoo';
    if (domain.includes('icloud.')) return 'icloud';
    if (domain.includes('aol.')) return 'aol';
    if (domain.includes('protonmail.')) return 'protonmail';

    return 'other';
  };

  // Compute provider breakdown from events
  const computeProviderBreakdown = useCallback((events) => {
    if (!events || events.length === 0) {
      setProviderBreakdown(null);
      return;
    }

    const providerStats = {};

    events.forEach(event => {
      const email = event.recipientEmail || event.email;
      const provider = extractProvider(email);

      if (!providerStats[provider]) {
        providerStats[provider] = {
          provider,
          sent: 0,
          delivered: 0,
          opened: 0,
          clicked: 0,
          bounced: 0
        };
      }

      // Count by event type
      const eventType = event.type || event.eventType || event.event;

      if (eventType === 'sent' || eventType === 'delivery_started') {
        providerStats[provider].sent++;
      } else if (eventType === 'delivered' || eventType === 'delivery_success') {
        providerStats[provider].delivered++;
      } else if (eventType === 'opened' || eventType === 'open') {
        providerStats[provider].opened++;
      } else if (eventType === 'clicked' || eventType === 'click') {
        providerStats[provider].clicked++;
      } else if (eventType === 'bounced' || eventType === 'bounce' || eventType === 'hard_bounce' || eventType === 'soft_bounce') {
        providerStats[provider].bounced++;
      }
    });

    // Convert to array and calculate percentages
    const breakdown = Object.values(providerStats).map(stats => ({
      ...stats,
      deliveredRate: stats.sent > 0 ? ((stats.delivered / stats.sent) * 100).toFixed(2) : 0,
      openRate: stats.sent > 0 ? ((stats.opened / stats.sent) * 100).toFixed(2) : 0,
      clickRate: stats.sent > 0 ? ((stats.clicked / stats.sent) * 100).toFixed(2) : 0,
      bounceRate: stats.sent > 0 ? ((stats.bounced / stats.sent) * 100).toFixed(2) : 0
    })).sort((a, b) => b.sent - a.sent);

    setProviderBreakdown(breakdown);
  }, []);

  // Compute trend data for line charts
  const computeTrendData = useCallback((events, metric) => {
    if (!events || events.length === 0) return [];

    const dateMap = new Map();

    events.forEach(event => {
      const date = event.timestamp ? new Date(event.timestamp).toISOString().split('T')[0] : null;
      if (!date) return;

      if (!dateMap.has(date)) {
        dateMap.set(date, { date, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 });
      }

      const data = dateMap.get(date);
      const eventType = event.type || event.eventType || event.event;

      if (eventType === 'sent' || eventType === 'delivery_started') {
        data.sent++;
      } else if (eventType === 'delivered' || eventType === 'delivery_success') {
        data.delivered++;
      } else if (eventType === 'opened' || eventType === 'open') {
        data.opened++;
      } else if (eventType === 'clicked' || eventType === 'click') {
        data.clicked++;
      } else if (eventType === 'bounced' || eventType === 'bounce') {
        data.bounced++;
      }
    });

    // Convert to array and sort by date
    const sortedData = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    // Calculate cumulative rates
    let totalSent = 0;
    let totalDelivered = 0;
    let totalOpened = 0;
    let totalClicked = 0;
    let totalBounced = 0;

    return sortedData.map((d, idx) => {
      totalSent += d.sent;
      totalDelivered += d.delivered;
      totalOpened += d.opened;
      totalClicked += d.clicked;
      totalBounced += d.bounced;

      const value = totalSent > 0
        ? ((metric === 'openRate' ? totalOpened : metric === 'clickRate' ? totalClicked : totalBounced) / totalSent * 100)
        : 0;

      return {
        date: d.date,
        [metric]: parseFloat(value.toFixed(2))
      };
    });
  }, []);

  // Load events when date range or selected campaign changes
  useEffect(() => {
    if (selectedCampaign) {
      console.log('[Dashboard] Loading events for campaign:', selectedCampaign.campaignId, 'dateRange:', dateRange, 'source:', selectedCampaign.source);
      loadCampaignEvents(selectedCampaign.campaignId);
    }
  }, [selectedCampaign, dateRange, loadCampaignEvents]);

  // Memoized trend data
  const openRateTrend = useMemo(() => computeTrendData(campaignEvents, 'openRate'), [campaignEvents, computeTrendData]);
  const clickRateTrend = useMemo(() => computeTrendData(campaignEvents, 'clickRate'), [campaignEvents, computeTrendData]);
  const bounceRateTrend = useMemo(() => computeTrendData(campaignEvents, 'bounceRate'), [campaignEvents, computeTrendData]);

  // Send statistics chart data (from historical stats)
  const sendStatsChartData = useMemo(() => {
    if (!historicalStats?.history) return [];

    return historicalStats.history.map(h => ({
      date: h.date,
      success: h.total || 0,
      // Assuming failure data might be available in the future
      failure: 0
    }));
  }, [historicalStats]);

  if (loading && vpsStats.campaigns.length === 0) {
    return (
      <div className="marketing-dashboard">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="marketing-dashboard">
      {/* DEPLOYMENT TIMESTAMP - PROVES NEW DEPLOYMENT IS LIVE */}
      <div style={{
        background: '#22c55e',
        color: 'white',
        padding: '10px 20px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px'
      }}>
        ✅ DEPLOYMENT v2.0 - JANUARY 12, 2026 @ 11:15 PM EST - NEW FEATURES ACTIVE
      </div>

      <div className="dashboard-header-with-controls">
        <div className="dashboard-title-section">
          <h1>📧 Marketing Campaigns</h1>
          <p>Create and manage LinkedIn and email marketing campaigns</p>
        </div>
        <div className="dashboard-controls">
          <button className="btn btn-secondary" onClick={() => navigate('/marketing/analytics')}>
            📊 Analytics
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="date-range-selector">
        <div className="date-range-tabs">
          <button
            className={`date-tab ${dateRange === 'all' ? 'active' : ''}`}
            onClick={() => setDateRange('all')}
          >All Time</button>
          <button
            className={`date-tab ${dateRange === '30days' ? 'active' : ''}`}
            onClick={() => setDateRange('30days')}
          >Last 30 Days</button>
          <button
            className={`date-tab ${dateRange === '7days' ? 'active' : ''}`}
            onClick={() => setDateRange('7days')}
          >Last 7 Days</button>
          <button
            className={`date-tab ${dateRange === 'yesterday' ? 'active' : ''}`}
            onClick={() => setDateRange('yesterday')}
          >Yesterday</button>
          <button
            className={`date-tab ${dateRange === 'today' ? 'active' : ''}`}
            onClick={() => setDateRange('today')}
          >Today</button>
        </div>
        <span className="date-range-display">{getDateRangeLabel()}</span>
      </div>

      <div className="dashboard-content">
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {/* All Campaigns Summary - Combined Panel */}
        <div className="global-stats-section">
          <h3>All Campaigns Summary</h3>
          {/* Campaign Status Row */}
          <div className="stats-grid campaign-status-grid">
            <div className="stat-card total">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <div className="stat-label">Total Campaigns</div>
                <div className="stat-value">{vpsStats.overview.totalCampaigns || vpsStats.campaigns.length}</div>
              </div>
            </div>
            <div className="stat-card scheduled">
              <div className="stat-icon">🔄</div>
              <div className="stat-content">
                <div className="stat-label">Active</div>
                <div className="stat-value">{vpsStats.campaigns.filter(c => c.status === 'active').length}</div>
              </div>
            </div>
            <div className="stat-card completed">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-label">Completed</div>
                <div className="stat-value">{vpsStats.campaigns.filter(c => c.status === 'completed').length}</div>
              </div>
            </div>
          </div>
          {/* Email Stats Row */}
          <div className="stats-grid email-stats-grid">
            <div className="stat-card sent">
              <div className="stat-icon">📤</div>
              <div className="stat-content">
                <div className="stat-label">Sent</div>
                <div className="stat-value">{vpsStats.overview.totalSent}</div>
              </div>
            </div>
            <div className="stat-card delivered">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-label">Delivered</div>
                <div className="stat-value">{vpsStats.overview.totalSent - vpsStats.overview.totalBounced}</div>
              </div>
            </div>
            <div className="stat-card bounced">
              <div className="stat-icon">↩️</div>
              <div className="stat-content">
                <div className="stat-label">Bounced</div>
                <div className="stat-value">{vpsStats.overview.totalBounced}</div>
              </div>
            </div>
            <div className="stat-card replied">
              <div className="stat-icon">💬</div>
              <div className="stat-content">
                <div className="stat-label">Replied</div>
                <div className="stat-value">{vpsStats.overview.totalReplied}</div>
              </div>
            </div>
            <div className="stat-card response-rate">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-label">Response Rate</div>
                <div className="stat-value">
                  {vpsStats.overview.totalSent > 0
                    ? ((vpsStats.overview.totalReplied / vpsStats.overview.totalSent) * 100).toFixed(2)
                    : 0}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Selector */}
        {vpsStats.campaigns.length > 0 && (
          <div className="campaign-selector-section">
            <h3>Campaigns ({vpsStats.campaigns.length})</h3>
            <div className="campaign-tabs">
              {vpsStats.campaigns.map((campaign, idx) => (
                <button
                  key={campaign.campaignId}
                  className={`campaign-tab ${idx === selectedCampaignIndex ? 'active' : ''}`}
                  onClick={() => setSelectedCampaignIndex(idx)}
                >
                  {campaign.name}
                  <span className="campaign-badge">{campaign.stats?.sent || 0} sent</span>
                </button>
              ))}
              <button className="campaign-tab add-campaign" onClick={handleCreateCampaign}>
                + New Campaign
              </button>
            </div>
          </div>
        )}

        {/* Selected Campaign Details */}
        {selectedCampaign && (
          <div className="vps-stats-section">
            <h2>{selectedCampaign.name}</h2>

            {/* Per-Campaign Stats */}
            <div className="stats-grid email-stats-grid">
              <div className="stat-card sent">
                <div className="stat-icon">📤</div>
                <div className="stat-content">
                  <div className="stat-label">Sent</div>
                  <div className="stat-value">{selectedCampaign.stats?.sent || 0}</div>
                </div>
              </div>
              <div className="stat-card delivered">
                <div className="stat-icon">✅</div>
                <div className="stat-content">
                  <div className="stat-label">Delivered</div>
                  <div className="stat-value">{selectedCampaign.stats?.delivered || 0}</div>
                </div>
              </div>
              <div className="stat-card bounced">
                <div className="stat-icon">↩️</div>
                <div className="stat-content">
                  <div className="stat-label">Bounced</div>
                  <div className="stat-value">{selectedCampaign.stats?.bounced || 0}</div>
                </div>
              </div>
              <div className="stat-card replied">
                <div className="stat-icon">💬</div>
                <div className="stat-content">
                  <div className="stat-label">Replied</div>
                  <div className="stat-value">{selectedCampaign.stats?.replied || 0}</div>
                </div>
              </div>
              <div className="stat-card response-rate">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <div className="stat-label">Response Rate</div>
                  <div className="stat-value">{selectedCampaign.stats?.responseRate || 0}%</div>
                </div>
              </div>
            </div>

            {/* NEW: Mail Provider Performance Table */}
            {!loadingCampaignData && providerBreakdown && providerBreakdown.length > 0 && (
              <div className="provider-section">
                <h3>Mail Provider Performance</h3>
                <div className="provider-table-container">
                  <table className="provider-table">
                    <thead>
                      <tr>
                        <th>Provider</th>
                        <th>Sent</th>
                        <th>Delivered</th>
                        <th>Opened</th>
                        <th>Clicked</th>
                        <th>Bounced</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providerBreakdown.map((provider) => (
                        <tr key={provider.provider}>
                          <td>
                            <span className="provider-name">
                              <span className={`provider-icon ${provider.provider}`}>
                                {provider.provider.charAt(0).toUpperCase()}
                              </span>
                              {provider.provider.charAt(0).toUpperCase() + provider.provider.slice(1)}
                            </span>
                          </td>
                          <td>{provider.sent.toLocaleString()}</td>
                          <td>
                            <span className={`metric-percent ${provider.deliveredRate >= 95 ? 'good' : provider.deliveredRate >= 85 ? 'warning' : 'bad'}`}>
                              {provider.deliveredRate}%
                            </span>
                          </td>
                          <td>
                            <span className={`metric-percent ${provider.openRate >= 20 ? 'good' : provider.openRate >= 10 ? 'warning' : 'bad'}`}>
                              {provider.openRate}%
                            </span>
                          </td>
                          <td>
                            <span className={`metric-percent ${provider.clickRate >= 2 ? 'good' : 'warning'}`}>
                              {provider.clickRate}%
                            </span>
                          </td>
                          <td>
                            <span className={`metric-percent ${provider.bounceRate <= 2 ? 'good' : provider.bounceRate <= 5 ? 'warning' : 'bad'}`}>
                              {provider.bounceRate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* NEW: Send Statistics Chart */}
            {!loadingCampaignData && sendStatsChartData.length > 0 && (
              <div className="send-stats-section">
                <div className="send-stats-header">
                  <h3>📊 Send Statistics</h3>
                </div>
                <div className="send-stats-summary">
                  <div className="send-stat-item">
                    <span className="send-stat-label">Success</span>
                    <span className="send-stat-value success">
                      {sendStatsChartData.reduce((sum, d) => sum + d.success, 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="send-stat-item">
                    <span className="send-stat-label">Total Sent</span>
                    <span className="send-stat-value neutral">
                      {sendStatsChartData.reduce((sum, d) => sum + d.success, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="chart-full-width">
                  <D3BarChart
                    data={sendStatsChartData}
                    xKey="date"
                    yKey="success"
                    xLabel="Date"
                    yLabel="Emails Sent"
                    color="#22c55e"
                    height="250px"
                    showGrid={true}
                  />
                </div>
              </div>
            )}

            {/* NEW: Analytics Overview with Trend Charts */}
            {!loadingCampaignData && (openRateTrend.length > 0 || clickRateTrend.length > 0 || bounceRateTrend.length > 0) && (
              <div className="analytics-overview">
                <h3>📈 Performance Trends ({getDateRangeLabel()})</h3>
                <div className="analytics-charts-row">
                  {/* Open Rate Trend */}
                  {openRateTrend.length > 0 && (
                    <div className="trend-card">
                      <div className="trend-header">
                        <span className="trend-title">Open Rate</span>
                        <span className="trend-value">{openRateTrend[openRateTrend.length - 1]?.openRate || 0}%</span>
                      </div>
                      <div className="trend-chart-container">
                        <D3LineChart
                          data={openRateTrend}
                          xKey="date"
                          yKey="openRate"
                          xLabel=""
                          yLabel=""
                          color="#22c55e"
                          height="120px"
                          showDots={false}
                          showGrid={false}
                        />
                      </div>
                    </div>
                  )}

                  {/* Click Rate Trend */}
                  {clickRateTrend.length > 0 && (
                    <div className="trend-card">
                      <div className="trend-header">
                        <span className="trend-title">Click Rate</span>
                        <span className="trend-value">{clickRateTrend[clickRateTrend.length - 1]?.clickRate || 0}%</span>
                      </div>
                      <div className="trend-chart-container">
                        <D3LineChart
                          data={clickRateTrend}
                          xKey="date"
                          yKey="clickRate"
                          xLabel=""
                          yLabel=""
                          color="#3b82f6"
                          height="120px"
                          showDots={false}
                          showGrid={false}
                        />
                      </div>
                    </div>
                  )}

                  {/* Bounce Rate Trend */}
                  {bounceRateTrend.length > 0 && (
                    <div className="trend-card">
                      <div className="trend-header">
                        <span className="trend-title">Bounce Rate</span>
                        <span className="trend-value">{bounceRateTrend[bounceRateTrend.length - 1]?.bounceRate || 0}%</span>
                      </div>
                      <div className="trend-chart-container">
                        <D3LineChart
                          data={bounceRateTrend}
                          xKey="date"
                          yKey="bounceRate"
                          xLabel=""
                          yLabel=""
                          color="#ef4444"
                          height="120px"
                          showDots={false}
                          showGrid={false}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Loading indicator for campaign-specific data */}
            {loadingCampaignData && (
              <div className="campaign-data-loading">
                <div className="loading-spinner"></div>
                <span>Loading campaign data...</span>
              </div>
            )}

            {/* Sending Plan Table */}
            {!loadingCampaignData && sendingPlan && sendingPlanParsed.rows.length > 0 && (
              <div className="sending-plan-section">
                <div className="sending-plan-header">
                  <h3>📅 Sending Schedule</h3>
                  <div className="sending-plan-actions">
                    <button className="btn btn-secondary btn-sm" onClick={handleDownloadCsv}>
                      📥 Download CSV
                    </button>
                    {sendingPlan.updatedAt && (
                      <span className="plan-updated">
                        Last updated: {new Date(sendingPlan.updatedAt).toLocaleString()} by {sendingPlan.updatedBy || 'unknown'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Today's Summary */}
                {getTodayRow() && (
                  <div className="today-summary">
                    <strong>Today ({getTodayRow().date}):</strong> {getTodayRow().total} emails scheduled
                    {getTodayRow().notes && <span className="notes"> - {getTodayRow().notes}</span>}
                  </div>
                )}

                <div className="sending-plan-table-container">
                  <table className="sending-plan-table">
                    <thead>
                      {(() => {
                        // Build domain groups from senders
                        const domainGroups = [];
                        let currentDomain = null;
                        let colspan = 0;
                        sendingPlanParsed.senders.forEach((sender, idx) => {
                          const domain = sender.split('@')[1] || '';
                          if (domain !== currentDomain) {
                            if (currentDomain !== null) {
                              domainGroups.push({ domain: currentDomain, colspan });
                            }
                            currentDomain = domain;
                            colspan = 1;
                          } else {
                            colspan++;
                          }
                          if (idx === sendingPlanParsed.senders.length - 1) {
                            domainGroups.push({ domain: currentDomain, colspan });
                          }
                        });

                        // Build IP groups from mailbox config
                        const mailboxes = sendingPlan?.mailboxConfig?.mailboxes || [];
                        const domainToIp = {};
                        mailboxes.forEach(mb => {
                          if (mb.domain && mb.ip) {
                            domainToIp[mb.domain] = mb.ip;
                          }
                        });

                        // Group domains by IP
                        const ipGroups = [];
                        let currentIp = null;
                        let ipColspan = 0;
                        domainGroups.forEach((dg, idx) => {
                          const ip = domainToIp[dg.domain] || 'Unknown';
                          if (ip !== currentIp) {
                            if (currentIp !== null) {
                              ipGroups.push({ ip: currentIp, colspan: ipColspan });
                            }
                            currentIp = ip;
                            ipColspan = dg.colspan;
                          } else {
                            ipColspan += dg.colspan;
                          }
                          if (idx === domainGroups.length - 1) {
                            ipGroups.push({ ip: currentIp, colspan: ipColspan });
                          }
                        });

                        return (
                          <>
                            {/* IP address meta-header row */}
                            <tr className="ip-header-row">
                              <th></th>
                              {ipGroups.map((group, idx) => (
                                <th key={idx} colSpan={group.colspan} className="ip-header">
                                  {group.ip}
                                </th>
                              ))}
                              <th></th>
                            </tr>
                            {/* Domain meta-header row */}
                            <tr className="domain-header-row">
                              <th></th>
                              {domainGroups.map((group, idx) => (
                                <th key={idx} colSpan={group.colspan} className="domain-header">
                                  {group.domain}
                                </th>
                              ))}
                              <th></th>
                            </tr>
                          </>
                        );
                      })()}
                      {/* Sender email header row */}
                      <tr>
                        <th>Date</th>
                        {sendingPlanParsed.senders.map((sender, idx) => (
                          <th key={idx} className="sender-header" title={sender}>
                            {sender.split('@')[0]}@
                          </th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getMergedSchedule().slice(0, 60).map((row, idx) => {
                        const today = new Date().toISOString().split('T')[0];
                        const isToday = row.date === today;
                        const isPast = row.date < today;
                        const rowClasses = [
                          isToday ? 'row-today' : '',
                          isPast && !row.isHistorical ? 'row-past' : '',
                          row.isHistorical ? 'row-historical' : ''
                        ].filter(Boolean).join(' ');
                        return (
                          <tr key={idx} className={rowClasses}>
                            <td className="date-cell">
                              {row.date}
                              {row.isHistorical && <span className="actual-badge">actual</span>}
                            </td>
                            {row.volumes.map((vol, vidx) => (
                              <td key={vidx} className="volume-cell">{vol}</td>
                            ))}
                            <td className="total-cell">{row.total}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {getMergedSchedule().length > 60 && (
                  <div className="plan-truncated">
                    Showing 60 of {getMergedSchedule().length} days. Download CSV for full schedule.
                  </div>
                )}
              </div>
            )}

            {/* Active Triggers Display */}
            {!loadingCampaignData && campaignTriggers?.triggers && campaignTriggers.triggers.length > 0 && selectedCampaign && (
              <div className="triggers-section">
                <h4>Active Safety Triggers</h4>
                {campaignTriggers.appliedPresetId && (
                  <div className="applied-preset-badge">
                    Applied Preset: {campaignTriggers.appliedPresetName || campaignTriggers.appliedPresetId}
                  </div>
                )}
                <div className="triggers-list">
                  {campaignTriggers.triggers.map((trigger, idx) => {
                    const status = getTriggerStatus(trigger, selectedCampaign);
                    return (
                      <div key={idx} className={`trigger-item trigger-${status.status}`}>
                        <span className="trigger-metric">{trigger.metric.replace(/_/g, ' ')}</span>
                        <span className="trigger-condition">{trigger.operator} {(trigger.threshold * 100).toFixed(2)}%</span>
                        <span className="trigger-action">{trigger.action}</span>
                        <span className="trigger-value">
                          Current: {(status.value * 100).toFixed(2)}%
                          {status.status === 'ok' && ' ✓'}
                          {status.status === 'warning' && ' ⚠️'}
                          {status.status === 'triggered' && ' ❌'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Warmup Presets Browser - Collapsible */}
            {presets.length > 0 && (
              <div className="presets-section">
                <div
                  className="presets-header"
                  onClick={() => setPresetsExpanded(!presetsExpanded)}
                >
                  <span>{presetsExpanded ? '▼' : '▶'} Warmup Presets ({presets.length} available)</span>
                  <button className="btn btn-sm btn-secondary">
                    {presetsExpanded ? 'Collapse' : 'Expand'}
                  </button>
                </div>

                {presetsExpanded && (
                  <div className="presets-content">
                    <div className="preset-filters">
                      <button
                        className={`preset-filter-btn ${presetFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setPresetFilter('all')}
                      >All</button>
                      <button
                        className={`preset-filter-btn safe ${presetFilter === 'safe' ? 'active' : ''}`}
                        onClick={() => setPresetFilter('safe')}
                      >★★★★★ Safe</button>
                      <button
                        className={`preset-filter-btn medium ${presetFilter === 'medium' ? 'active' : ''}`}
                        onClick={() => setPresetFilter('medium')}
                      >★★★☆☆ Medium</button>
                      <button
                        className={`preset-filter-btn aggressive ${presetFilter === 'aggressive' ? 'active' : ''}`}
                        onClick={() => setPresetFilter('aggressive')}
                      >★☆☆☆☆ Aggressive</button>
                    </div>

                    <div className="preset-cards">
                      {filteredPresets.map((preset) => (
                        <div key={preset.presetId} className={`preset-card risk-${preset.riskTrack}`}>
                          <h4>{preset.name}</h4>
                          <div className="preset-risk">
                            <span className="risk-stars">{getRiskStars(preset.riskTrack)}</span>
                            <span className="risk-label">{getRiskLabel(preset.riskTrack)}</span>
                          </div>
                          <div className="preset-meta">
                            <span>{preset.durationWeeks} weeks</span>
                            <span>{preset.mailboxesRecommended?.min}-{preset.mailboxesRecommended?.max} mailboxes</span>
                            <span>Max {preset.maxVolumePerMailbox}/day/mailbox</span>
                          </div>
                          {preset.description && (
                            <p className="preset-description">{preset.description}</p>
                          )}
                          <div className="preset-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleViewPreset(preset)}
                            >View</button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleApplyPreset(preset)}
                            >Apply</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Email Template Section - inside VPS panel */}
            {!loadingCampaignData && campaignTemplate && (
              <div className="template-section">
                <div
                  className="template-header"
                  onClick={() => setTemplateExpanded(!templateExpanded)}
                >
                  <span>{templateExpanded ? '▼' : '▶'} Email Template</span>
                  <button className="btn btn-sm btn-secondary">
                    {templateExpanded ? 'Collapse' : 'Expand'}
                  </button>
                </div>

                {templateExpanded && (
                  <div className="template-content">
                    <div className="template-preview">
                      <div className="template-field">
                        <h4>Subject Line</h4>
                        <div className="template-subject">{campaignTemplate.subjectTemplate}</div>
                      </div>

                      <div className="template-field">
                        <h4>Email Body</h4>
                        <pre className="template-body">{campaignTemplate.bodyTemplate}</pre>
                      </div>

                      {campaignTemplate.variables && campaignTemplate.variables.length > 0 && (
                        <div className="template-field">
                          <h4>Template Variables</h4>
                          <table className="template-variables-table">
                            <thead>
                              <tr>
                                <th>Variable</th>
                                <th>Source Field</th>
                                <th>Format</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaignTemplate.variables.map((v, idx) => (
                                <tr key={idx}>
                                  <td><code>{`{{${v.name}}}`}</code></td>
                                  <td><code>{v.source}</code></td>
                                  <td>{v.format}</td>
                                  <td>{v.description || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {campaignTemplate.signature && (
                        <div className="template-field">
                          <h4>Signature</h4>
                          <div className="template-signature">
                            <div>{campaignTemplate.signature.name}</div>
                            <div>{campaignTemplate.signature.title}</div>
                            <div>{campaignTemplate.signature.phone}</div>
                          </div>
                        </div>
                      )}

                      <div className="template-meta">
                        <span>Source: {campaignTemplate.source || 'manual'}</span>
                        {campaignTemplate.extractedFrom && (
                          <span>Extracted from: {campaignTemplate.extractedFrom}</span>
                        )}
                        <span>Last updated: {campaignTemplate.updatedAt ? new Date(campaignTemplate.updatedAt).toLocaleString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preset Detail Modal */}
        {showPresetModal && selectedPreset && (
          <div className="modal-overlay" onClick={() => setShowPresetModal(false)}>
            <div className="modal-content preset-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedPreset.name}</h3>
                <button className="modal-close" onClick={() => setShowPresetModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="preset-detail-meta">
                  <span className="risk-badge risk-{selectedPreset.riskTrack}">
                    {getRiskStars(selectedPreset.riskTrack)} {getRiskLabel(selectedPreset.riskTrack)}
                  </span>
                  <span>Duration: {selectedPreset.durationWeeks} weeks</span>
                  <span>Max: {selectedPreset.maxVolumePerMailbox}/day/mailbox</span>
                </div>

                {selectedPreset.description && (
                  <p className="preset-detail-description">{selectedPreset.description}</p>
                )}

                <h4>Schedule Preview</h4>
                <div className="schedule-preview">
                  <table className="schedule-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>%</th>
                        <th>Vol (at {selectedPreset.maxVolumePerMailbox} max)</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPreset.schedule?.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.day}</td>
                          <td>{item.pct}%</td>
                          <td>{Math.round(selectedPreset.maxVolumePerMailbox * item.pct / 100)}</td>
                          <td>{item.notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h4>Safety Triggers</h4>
                <div className="triggers-preview">
                  {selectedPreset.triggers?.map((trigger, idx) => (
                    <div key={idx} className={`trigger-preview-item action-${trigger.action}`}>
                      <span className="trigger-condition">
                        {trigger.metric.replace(/_/g, ' ')} {trigger.operator} {(trigger.threshold * 100).toFixed(2)}%
                      </span>
                      <span className="trigger-action-badge">{trigger.action.toUpperCase()}</span>
                      <span className="trigger-message">{trigger.message}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPresetModal(false)}>Close</button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowPresetModal(false);
                    setShowApplyFlow(true);
                  }}
                >Apply to Campaign</button>
              </div>
            </div>
          </div>
        )}

        {/* Apply Preset Flow Modal */}
        {showApplyFlow && selectedPreset && selectedCampaign && (
          <ApplyPresetModal
            preset={selectedPreset}
            campaign={selectedCampaign}
            senders={sendingPlanParsed.senders}
            onClose={() => {
              setShowApplyFlow(false);
              setSelectedPreset(null);
            }}
            onApply={handleConfirmApply}
            applying={applyingPreset}
            getRiskStars={getRiskStars}
            getRiskLabel={getRiskLabel}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;

